'use client';
import React from 'react';

interface Testimonial {
  id: number;
  quote: string;
  author: string;
  position: string;
  company: string;
  rating: number;
  image: string;
}

const Testimonials: React.FC = () => {
  const [mousePos, setMousePos] = React.useState({ x: 0, y: 0 });
  const sectionRef = React.useRef<HTMLElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const testimonials: Testimonial[] = [
    {
      id: 1,
      quote: "Agora todo o time consegue acompanhar o atendimento em tempo real, sem perder nenhuma conversa importante.",
      author: "Camila Faria",
      position: "Coordenadora de Atendimento",
      company: "Grupo Nova Era",
      rating: 5,
      image: "https://randomuser.me/api/portraits/women/44.jpg"
    },
    {
      id: 2,
      quote: "A organização das conversas melhorou muito. Ficou fácil saber quem está atendendo cada cliente.",
      author: "Rafael Silva",
      position: "Supervisor de Suporte",
      company: "Connect Brasil",
      rating: 5,
      image: "https://randomuser.me/api/portraits/men/32.jpg"
    },
    {
      id: 3,
      quote: "Nossa produtividade aumentou porque todos têm acesso ao histórico e podem colaborar juntos.",
      author: "Marina Costa",
      position: "Gerente de Vendas",
      company: "System One",
      rating: 5,
      image: "https://randomuser.me/api/portraits/women/12.jpg"
    },
    {
      id: 4,
      quote: "Com a centralização, não temos mais informações perdidas em grupos ou celulares pessoais.",
      author: "João Mendes",
      position: "Fundador",
      company: "Centraliza Pro",
      rating: 5,
      image: "https://randomuser.me/api/portraits/men/65.jpg"
    },
    {
      id: 5,
      quote: "O fluxo de trabalho ficou mais claro e conseguimos integrar novos membros do time rapidamente.",
      author: "Ana Beatriz",
      position: "COO",
      company: "Viva Digital",
      rating: 5,
      image: "https://randomuser.me/api/portraits/women/68.jpg"
    },
    {
      id: 6,
      quote: "Reduzimos o retrabalho, pois todos veem o que já foi resolvido e o que está pendente.",
      author: "Carlos Eduardo",
      position: "Gerente Comercial",
      company: "Brasil Contact",
      rating: 5,
      image: "https://randomuser.me/api/portraits/men/77.jpg"
    },
    {
      id: 7,
      quote: "A ferramenta facilitou a comunicação entre setores, melhorando o atendimento ao cliente.",
      author: "Lucia Santos",
      position: "Gerente de Atendimento",
      company: "Rede União",
      rating: 5,
      image: "https://randomuser.me/api/portraits/women/21.jpg"
    },
    {
      id: 8,
      quote: "Agora consigo acompanhar o desempenho do time e identificar pontos de melhoria facilmente.",
      author: "Roberto Lima",
      position: "Diretor Comercial",
      company: "Horizonte Consultoria",
      rating: 5,
      image: "https://randomuser.me/api/portraits/men/41.jpg"
    }
  ];

  // Duplicar testimonials para efeito infinito
  const firstRow = testimonials.slice(0, 4);
  const secondRow = testimonials.slice(4, 8);

  const TestimonialCard = ({ testimonial }: { testimonial: Testimonial }) => (
    <div className="bg-white rounded-2xl p-5 w-[280px] sm:w-[320px] mx-3 h-[200px] flex flex-col shadow-sm border border-gray-100 flex-shrink-0">
      {/* Quote */}
      <div className="flex-1 flex items-start mb-3 overflow-hidden">
        <blockquote className="relative w-full">
          <p className="text-gray-700 text-xs leading-relaxed font-medium line-clamp-3">
            "{testimonial.quote}"
          </p>
        </blockquote>
      </div>

      {/* Footer - Informações do avaliador */}
      <div className="flex items-center space-x-3 pt-2 border-t border-gray-200 flex-shrink-0">
        <div className="relative flex-shrink-0">
          <img
            src={testimonial.image}
            alt={`Foto de ${testimonial.author}`}
            className="w-10 h-10 rounded-full object-cover"
          />
          {/* Badge de verificação */}
          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
            <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
            </svg>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-gray-900 text-xs truncate">
            {testimonial.author}
          </h4>
          <p className="text-gray-600 text-xs leading-tight truncate">
            {testimonial.position}
          </p>
          <p className="text-green-600 font-semibold text-xs truncate">
            {testimonial.company}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* CSS Animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes marquee {
            0% {
              transform: translateX(0%);
            }
            100% {
              transform: translateX(-33.333333%);
            }
          }

          @keyframes marquee-reverse {
            0% {
              transform: translateX(-33.333333%);
            }
            100% {
              transform: translateX(0%);
            }
          }

          .animate-marquee {
            animation: marquee 60s linear infinite;
          }

          .animate-marquee-reverse {
            animation: marquee-reverse 60s linear infinite;
          }

          .line-clamp-4 {
            display: -webkit-box;
            -webkit-line-clamp: 4;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .line-clamp-3 {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        `
      }} />

      <section 
        id="testimonials" 
        ref={sectionRef}
        onMouseMove={handleMouseMove}
        className="relative py-20 bg-slate-50 overflow-hidden border-t border-b border-gray-100 group/testimonials"
      >
        {/* Fundo Estilizado - Neutro */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_#f1f5f9_0%,_transparent_100%)]" />
          
          {/* Grid Base - Mais visível quando apagado */}
          <div className="absolute inset-0 opacity-[0.04]" 
            style={{ 
              backgroundImage: `
                linear-gradient(to right, #3b82f6 1px, transparent 1px),
                linear-gradient(to bottom, #3b82f6 1px, transparent 1px)
              `, 
              backgroundSize: '40px 40px' 
            }} 
          />

          {/* Grid Hover - Mais suave e com mais blur */}
          <div className="absolute inset-0 opacity-0 group-hover/testimonials:opacity-[0.12] transition-opacity duration-700" 
            style={{ 
              backgroundImage: `
                linear-gradient(to right, #3b82f6 1px, transparent 1px),
                linear-gradient(to bottom, #3b82f6 1px, transparent 1px)
              `, 
              backgroundSize: '40px 40px',
              WebkitMaskImage: `radial-gradient(circle 350px at ${mousePos.x}px ${mousePos.y}px, black 0%, transparent 100%)`,
              maskImage: `radial-gradient(circle 350px at ${mousePos.x}px ${mousePos.y}px, black 0%, transparent 100%)`,
            }} 
          />
        </div>

        <div className="relative z-10 max-w-full">
          {/* Header */}
          <div className="text-center mb-16 px-4">
            <h2 className="text-2xl lg:text-4xl font-bold text-gray-900 mb-6">
              Veja a opinião de quem usa
            </h2>
            <p className="text-base text-gray-700 max-w-2xl mx-auto">
              Depoimentos reais de empresas que transformaram seus resultados
            </p>
          </div>

          {/* Marquee Testimonials */}
          <div className="relative">
            {/* Primeira linha - movimento para direita */}
            <div className="mb-6">
              <div className="flex animate-marquee">
                {[...firstRow, ...firstRow, ...firstRow].map((testimonial, index) => (
                  <TestimonialCard key={`row1-${testimonial.id}-${index}`} testimonial={testimonial} />
                ))}
              </div>
            </div>

            {/* Segunda linha - movimento para esquerda */}
            <div>
              <div className="flex animate-marquee-reverse">
                {[...secondRow, ...secondRow, ...secondRow].map((testimonial, index) => (
                  <TestimonialCard key={`row2-${testimonial.id}-${index}`} testimonial={testimonial} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Testimonials; 
